import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Zap, CheckCircle, Loader2, ArrowRight, Headphones } from 'lucide-react';

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const noUpsell = searchParams.get('no_upsell');
  const isUpsell = searchParams.get('upsell');
  const parentSession = searchParams.get('parent_session');
  const declinedOffer = searchParams.get('declined_offer');

  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }

    // If user declined an offer from the Stripe cancel URL, record it and check for more
    if (declinedOffer) {
      api.post('/upsell/decline', { offer_id: declinedOffer, session_id: sessionId }).catch(() => {});
    }

    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          // Check for post-purchase upsells (unless already shown or this is final)
          if (!noUpsell && !declinedOffer) {
            try {
              const upsellRes = await api.get(`/upsell/for-session/${isUpsell && parentSession ? parentSession : sessionId}`);
              if (upsellRes.data.offers?.length > 0) {
                navigate(`/upsell?session_id=${isUpsell && parentSession ? parentSession : sessionId}`, { replace: true });
                return;
              }
            } catch {}
          }
          // Meta Pixel: Purchase
          if (window.fbq) window.fbq('track', 'Purchase', { value: data.amount_total || 0, currency: data.currency || 'USD' });
          setStatus('success');
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else {
          setAttempts(prev => {
            if (prev >= 8) { setStatus('timeout'); return prev; }
            setTimeout(poll, 2000);
            return prev + 1;
          });
        }
      } catch {
        setStatus('error');
      }
    };
    poll();
  }, [sessionId, noUpsell, isUpsell, parentSession, declinedOffer, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" data-testid="checkout-success">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center space-y-4">
          {status === 'checking' && (
            <>
              <Loader2 className="w-12 h-12 text-[#1A9DA5] animate-spin mx-auto" />
              <h1 className="text-2xl font-bold font-['Outfit']">Processing Payment...</h1>
              <p className="text-muted-foreground">Please wait while we confirm your purchase.</p>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold font-['Outfit']">
                {isUpsell ? 'All Set!' : 'Payment Successful!'}
              </h1>
              <p className="text-muted-foreground">
                {isUpsell
                  ? 'Your purchases have been added to your library. Enjoy!'
                  : 'Your guide is ready. Check your email for access or go directly to your portal.'
                }
              </p>

              {isUpsell && (
                <div className="p-3 rounded-lg bg-[#1A9DA5]/5 border border-[#1A9DA5]/20">
                  <div className="flex items-center gap-2 text-sm">
                    <Headphones className="w-4 h-4 text-[#1A9DA5]" />
                    <span className="font-medium">Bonus content added to your library</span>
                  </div>
                </div>
              )}

              <div className="flex gap-3 justify-center pt-2">
                <Link to="/portal">
                  <Button data-testid="goto-portal-btn" className="bg-[#1A9DA5] hover:bg-[#158D92] text-white">
                    <Headphones className="w-4 h-4 mr-2" />Go to My Guides
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline">Back to Store</Button>
                </Link>
              </div>
            </>
          )}
          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <>
              <img src="/logo.png" alt="Wayfinder" className="w-12 h-12 mx-auto rounded-xl" />
              <h1 className="text-2xl font-bold font-['Outfit']">
                {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
              </h1>
              <p className="text-muted-foreground">Please check your email for confirmation or contact support.</p>
              <Link to="/"><Button variant="outline">Back to Store</Button></Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
